import type { RuleGroupView } from '@tabel/shared/schemas/rules';
import { type FieldIndex, type Values, plainAnswer, sameValue } from './answer';
import { visibleField } from './edit';
import { LABEL, T } from './texts';

/*
 * Каталог правил (004, US6, T100; research R15; FR-054–FR-056): правила, которые объект не применяет, — ответ «Не
 * применять» (`offWhen`) или нет нужных данных (`unmetNeeds`). Правил закона и правил системы в каталоге нет. Правила
 * «скоро» не бывает: на экран попадают только правила, которые движок умеет считать. «Добавить» включает правило
 * первым значением, кроме «Не применять» (R15).
 */

/** Чего не хватает объекту: графики, производственный календарь, документы, отметки бригадира. */
const NEEDS = ['schedules', 'calendar', 'documents', 'marks'] as const;
type Need = (typeof NEEDS)[number] | 'other';

/** Подпись группы каталога и текст у правила вместо «Добавить» (сценарий US6 3). */
export const NEED_TEXT: Readonly<Record<Need, { readonly group: string; readonly rule: string }>> = {
  schedules: { group: LABEL.needSchedules, rule: T.needSchedules },
  calendar: { group: LABEL.needCalendar, rule: T.needCalendar },
  documents: { group: LABEL.needDocuments, rule: T.needDocuments },
  marks: { group: LABEL.needMarks, rule: T.needMarks },
  other: { group: LABEL.needOther, rule: T.needOther },
};

/** Первая недостающая вещь по порядку: по ней правило стоит в группе каталога. */
function firstNeed(g: RuleGroupView): Need | null {
  if (g.unmetNeeds.length === 0) return null;
  return NEEDS.find((n) => g.unmetNeeds.includes(n)) ?? 'other';
}

/** Правило в каталоге: объект его не применяет; закон и правила системы — не в каталоге. */
export const isCatalog = (g: RuleGroupView) => g.inCatalog && !g.law && !g.fixed;

export interface Catalog {
  /** «Можно добавить». */
  readonly can: readonly RuleGroupView[];
  /** Группы «Сначала нужно…» по порядку; пустых нет (FR-054). */
  readonly needs: readonly { readonly need: Need; readonly groups: readonly RuleGroupView[] }[];
}

export function catalogOf(groups: readonly RuleGroupView[]): Catalog {
  const list = groups.filter(isCatalog).sort((a, b) => a.order - b.order);
  const needs = ([...NEEDS, 'other'] as const)
    .map((need) => ({ need, groups: list.filter((g) => firstNeed(g) === need) }))
    .filter((x) => x.groups.length > 0);
  return { can: list.filter((g) => firstNeed(g) === null), needs };
}

/** Значение «Не применять» у этого поля правила. */
export function isOffValue(g: RuleGroupView, key: string, value: unknown): boolean {
  return g.offWhen?.field === key && sameValue(value, g.offWhen.value);
}

/**
 * Значения, с которыми «Добавить» открывает правило: поле `offWhen` — первое значение, кроме «Не применять»; у поля
 * «да/нет» — противоположное. Нет вариантов — `null`: правило откроется с прежними значениями. Поле выбора, которое
 * при этом ответе видно, но значения у него нет (ни в правилах, ни умолчания схемы), — тоже первым вариантом (R15):
 * иначе поле числа внутри варианта не видно, пока вариант не выбран (короткий визит: «время ровно на пороге» и порог,
 * T106). Числа не подставляются: их задаёт администратор.
 */
export function addValues(g: RuleGroupView): Values | null {
  const off = g.offWhen;
  if (!off) return null;
  const f = g.fields.find((x) => `${x.cube}.${x.name}` === off.field);
  let seed: Record<string, unknown>;
  if (f?.control === 'boolean' || typeof off.value === 'boolean') seed = { [off.field]: !off.value };
  else {
    const first = f?.options?.find((o) => !sameValue(o.value, off.value));
    if (!first) return null;
    seed = { [off.field]: first.value };
  }
  const values: Record<string, unknown> = { ...g.values, ...seed };
  for (const x of g.fields) {
    const key = `${x.cube}.${x.name}`;
    const option = x.options?.[0];
    if (x.control !== 'select' || option === undefined || values[key] !== undefined) continue;
    if (!visibleField(x, values)) continue;
    seed[key] = option.value;
    values[key] = option.value;
  }
  return seed;
}

/** Правило уже включено в черновике: значение поля `offWhen` в черновике — не «Не применять». */
export function addedInDraft(g: RuleGroupView): boolean {
  const off = g.offWhen;
  if (!off || !g.draft?.changed) return false;
  return !sameValue(g.draft.values[off.field], off.value);
}

/** Одно предложение, что правило делает (FR-054): текст варианта, которым правило включается, иначе — ответ. */
export function catalogSentence(g: RuleGroupView, fields: FieldIndex): string {
  const seed = addValues(g);
  const off = g.offWhen;
  if (seed && off) {
    const f = g.fields.find((x) => `${x.cube}.${x.name}` === off.field);
    const doc = f?.options?.find((o) => sameValue(o.value, seed[off.field]))?.doc;
    if (doc) return doc;
  }
  return plainAnswer(g, fields, seed ? { ...g.values, ...seed } : undefined).trim();
}
